/**
 * JSON Format Tool
 * 
 * Formats JSON with configurable indentation.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

export const JsonFormatTool: ITransformerTool = {
  id: 'json-format',
  name: 'JSON Format',
  description: 'Format and prettify JSON with configurable indentation',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['json', 'format', 'prettify', 'indent', 'beautify'],

  seo: {
    slug: 'json-formatter',
    pageTitle: 'JSON Formatter & Beautifier - Free Online Tool',
    metaDescription: 'Format, validate, and beautify JSON data online. Configurable indentation with optional key sorting. Fast, private, runs entirely in your browser.',
    keywords: ['json formatter', 'json beautifier', 'json prettify', 'format json online', 'json validator'],
    relatedTools: ['json-minify', 'json-validate', 'json-to-yaml'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.JSON],
  outputType: SEMANTIC_TYPES.JSON,

  attributes: [
    {
      name: 'indentSize',
      type: 'select',
      label: 'Indent Size',
      defaultValue: '2',
      options: ['2', '4', 'tab'],
      description: 'Number of spaces or tab for indentation',
    },
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  supportsInput(input: string, detectedType?: string): boolean {
    if (detectedType !== 'JSON') return false;
    try {
      parseJsonLenient(input);
      return true;
    } catch {
      return false;
    }
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let parsed: unknown;
    
    try {
      parsed = parseJsonLenient(input);
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid JSON: ${error.message}`, {
        suggestion: 'Check for missing commas, quotes, or brackets',
      });
    }

    // Sort keys if requested
    if (config.sortKeys) {
      parsed = sortObjectKeys(parsed);
    }

    // Determine indent
    const indentSize = config.indentSize as string || '2';
    const indent = indentSize === 'tab' ? '\t' : parseInt(indentSize, 10);

    return JSON.stringify(parsed, null, indent);
  },
};

/**
 * Recursively sort object keys alphabetically.
 */
function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }
  
  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    for (const key of keys) {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }
  
  return obj;
}

